import React, {forwardRef, useEffect, useImperativeHandle} from "react";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Typography
} from "@mui/material";
import {Controller, useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import FormLabelWithTooltip from "../form-label-with-tooltip";
import I18nTextField from "../i18n-text-field";
import AiSearchEnginesDialog from "./AiSearchEnginesDialog";
import {AI_ASSISTANT_SEARCH_ENGINES, isOpenAI} from "./constants";
import {fetchSearchEngines, showSearchEngineListModal} from "../../state/ai-assistant/aiAssistantAction";
import {aiNodeSettingsSelector} from "../../state/ai-assistant/aiAssistantSelector";
import {languageSelector, languagesSelector} from "../../state/app/appSelectors";
import {externalServicesSelector} from "../../state/config/configSelectors";
import {localizeI18nObj} from "../../utils/i18n";

const sectionsTitleStyle = {
  fontSize: "18px",
  color: "rgba(0, 0, 0, 0.6)",
  marginBottom: "8px"
};

const fieldStyle = {
  margin: "8px 0",
  "& > .MuiFormControlLabel-root": {
    marginTop: "0px"
  }
};

const paperStyle = {
  padding: "16px",
  marginBottom: "16px"
};

const DEFAULT_RETRY_MESSAGE = {
  it: "La query odata che hai generato è sintatticamente sbagliata e va in errore. Devi rigenerare la query attenendoti alle regole che ti ho indicato. Di seguito l'errore che genera la tua query: {ErrorMessage}. Non includere nella tua risposta informazioni che facciano capire che hai corretto la query.",
  en: "The query you generated is syntactically incorrect and will result in an error. You must regenerate the query according to the rules I have indicated. Below is the error generated by your query: {ErrorMessage}. Do not include information in your response that indicates you have corrected the query."
};

const AiSettingsForm = forwardRef(({initialData, onSubmit}, ref) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const language = useSelector(languageSelector);
  const languages = useSelector(languagesSelector);
  const baseUrl = useSelector(externalServicesSelector)?.ai;
  const {nodeId, searchEngines, authToken} = useSelector(aiNodeSettingsSelector);

  useEffect(() => {
    if (nodeId && !searchEngines) {
      dispatch(fetchSearchEngines(nodeId, baseUrl, authToken));
    }
  }, [nodeId, searchEngines, baseUrl, authToken, dispatch]);

  const sectionLabels = {
    metadata: t("modules.aiAssistant.aiSettings.form.Metadata"),
    analyze: t("modules.aiAssistant.aiSettings.form.Analyze"),
    search: t("modules.aiAssistant.aiSettings.form.Search"),
    oDataSearch: t("modules.aiAssistant.aiSettings.form.ODataSearch")
  };

  const {handleSubmit, control, watch, setValue} = useForm({
    defaultValues: {
      ...(initialData || {}),
      oDataSearch: {
        ...(initialData?.oDataSearch || {}),
        retryMessage: initialData?.oDataSearch?.retryMessage ?? DEFAULT_RETRY_MESSAGE
      }
    }
  });

  const settings = watch();

  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit(onSubmit)()
  }));

  const renderSection = (name, config) => {
    const section = settings[name];

    const isDisabled = !section?.enabled;

    const selectedEngine = searchEngines.find(engine => engine.id === section?.engineId);

    return (
      <Paper
        variant="outlined"
        className={`ai-settings-form__section ai-settings-form__section--${name}`}
        sx={paperStyle}
      >
        <Typography
          variant="h6"
          className={`ai-settings-form__section-title ai-settings-form__section-title--${name}`}
          gutterBottom
        >
          {sectionLabels[name]}
        </Typography>

        {/* Enable/Disable */}
        <Controller
          name={`${name}.enabled`}
          control={control}
          render={({field}) => (
            <FormControlLabel
              className={`ai-settings-form__section__enabled ai-settings-form__section__enabled--${name}`}
              control={<Checkbox {...field} checked={field.value || false} />}
              label={t("modules.aiAssistant.aiSettings.form.enable")}
            />
          )}
        />

        {/* Search Engine */}
        <Controller
          name={`${name}.engineId`}
          control={control}
          rules={{validate: val => (!isDisabled ? val || t("commons.validation.required") : true)}}
          render={({field, fieldState}) => (
            <TextField
              {...field}
              select
              required
              fullWidth
              variant="outlined"
              className={`ai-settings-form__section__engineId ai-settings-form__section__engineId--${name}`}
              sx={fieldStyle}
              label={t("modules.aiAssistant.aiSettings.form.searchEngine")}
              value={field.value || ""}
              onChange={({target}) => {
                if (target.value === "__create__") {
                  dispatch(showSearchEngineListModal());
                  return;
                }
                field.onChange(target.value);
                setValue(`${name}.assistantId`, null);
                setValue(`${name}.prePrompt`, null);
                setValue(`${name}.vectorStoreId`, null);
              }}
              disabled={isDisabled}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            >
              {searchEngines.map(engine => (
                <MenuItem key={engine.id} value={engine.id}>
                  <span style={{width: "100%"}}>{localizeI18nObj(engine.label, language, languages)}</span>
                </MenuItem>
              ))}
              <Divider sx={{my: 1}} />
              <MenuItem
                value="__create__"
                className={`ai-settings-form__section__engineId__manage-engine-menu-item ai-settings-form__section__engineId__manage-engine-menu-item--${name}`}
              >
                <ListItemIcon>
                  <EditIcon fontSize="small" sx={{color: theme => theme.palette.text.primary}} />
                </ListItemIcon>
                <ListItemText>
                  <em>{t("modules.aiAssistant.aiSettings.form.manageSearchEngines")}</em>
                </ListItemText>
              </MenuItem>
            </TextField>
          )}
        />

        {/* Assistant/Prompt ID (if openai) or PrePrompt */}
        {isOpenAI(selectedEngine?.type) ? (
          <>
            <Controller
              name={`${name}.assistantId`}
              control={control}
              rules={{required: !isDisabled && t("commons.validation.required")}}
              render={({field, fieldState}) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  variant="outlined"
                  className={`ai-settings-form__section__assistantId ai-settings-form__section__assistantId--${name}`}
                  sx={fieldStyle}
                  label={
                    selectedEngine?.type === AI_ASSISTANT_SEARCH_ENGINES.openai
                      ? t("modules.aiAssistant.aiSettings.form.assistantId")
                      : t("modules.aiAssistant.aiSettings.form.promptId")
                  }
                  value={field.value || ""}
                  onChange={val => field.onChange(val)}
                  disabled={isDisabled}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            {config?.vectorStoreId && selectedEngine?.type === AI_ASSISTANT_SEARCH_ENGINES.openaiResponses && (
              <Controller
                name={`${name}.vectorStoreId`}
                control={control}
                rules={{required: !isDisabled && t("commons.validation.required")}}
                render={({field, fieldState}) => (
                  <TextField
                    {...field}
                    required
                    fullWidth
                    variant="outlined"
                    className={`ai-settings-form__section__vectorStoreId ai-settings-form__section__vectorStoreId--${name}`}
                    sx={fieldStyle}
                    label={t("modules.aiAssistant.aiSettings.form.vectorStoreId")}
                    value={field.value || ""}
                    onChange={val => field.onChange(val)}
                    disabled={isDisabled}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
          </>
        ) : (
          <Controller
            name={`${name}.prePrompt`}
            control={control}
            rules={{required: !isDisabled && t("commons.validation.required")}}
            render={({field, fieldState}) => (
              <I18nTextField
                required
                {...field}
                fullWidth
                variant="outlined"
                multiline
                minRows={3}
                className={`ai-settings-form__section__prePrompt ai-settings-form__section__prePrompt--${name}`}
                sx={fieldStyle}
                label={t("modules.aiAssistant.aiSettings.form.prePrompt")}
                value={field.value || {}}
                onChange={val => field.onChange(val)}
                error={!!fieldState.error}
                disabled={isDisabled}
                helperText={fieldState.error?.message}
              />
            )}
          />
        )}

        {/* RetryCount + RetryMessage (only ODataSearch) */}
        {config?.retry && isOpenAI(selectedEngine?.type) && (
          <>
            <Controller
              name={`${name}.retryCount`}
              control={control}
              render={({field}) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  variant="outlined"
                  className={`ai-settings-form__section__retryCount ai-settings-form__section__retryCount--${name}`}
                  sx={fieldStyle}
                  label={t("modules.aiAssistant.aiSettings.form.retryCount")}
                  value={field.value ?? 0}
                  onChange={e => field.onChange(Number(e.target.value))}
                  disabled={isDisabled}
                >
                  {[0, 1, 2, 3, 4, 5].map(count => (
                    <MenuItem key={count} value={count}>
                      {count}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name={`${name}.retryMessage`}
              control={control}
              render={({field, fieldState}) => (
                <I18nTextField
                  {...field}
                  fullWidth
                  variant="outlined"
                  multiline
                  minRows={3}
                  className={`ai-settings-form__section__retryMessage ai-settings-form__section__retryMessage--${name}`}
                  sx={fieldStyle}
                  label={
                    <FormLabelWithTooltip tooltip={t("modules.aiAssistant.aiSettings.form.retryMessage.tooltip")}>
                      {t("modules.aiAssistant.aiSettings.form.retryMessage.label")}
                    </FormLabelWithTooltip>
                  }
                  value={field.value || {}}
                  onChange={val => field.onChange(val)}
                  disabled={isDisabled}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </>
        )}
      </Paper>
    );
  };

  return (
    searchEngines && (
      <>
        <Box>
          <Typography
            className="ai-settings-form__sections-title ai-settings-form__sections-title--catalog"
            sx={sectionsTitleStyle}
          >
            {t("modules.aiAssistant.aiSettings.form.tabs.catalog")}
          </Typography>
          {renderSection("search", {vectorStoreId: true})}
          <Typography
            className="ai-settings-form__sections-title ai-settings-form__sections-title--dataset"
            sx={sectionsTitleStyle}
          >
            {t("modules.aiAssistant.aiSettings.form.tabs.dataset")}
          </Typography>
          {renderSection("metadata")}
          {renderSection("oDataSearch", {retry: true})}
          {renderSection("analyze")}
        </Box>

        <AiSearchEnginesDialog searchEngines={searchEngines} settings={settings} />
      </>
    )
  );
});

export default AiSettingsForm;
